import React, { useState } from 'react';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xjgqzqpz';

const Contact: React.FC = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(false);

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (!res.ok) throw new Error(`Form submission failed: ${res.status}`);

      setFormSubmitted(true);
      setFormData({ name: '', email: '', phone: '', projectType: '', message: '' });
      setTimeout(() => setFormSubmitted(false), 8000);
    } catch (err) {
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNewsletterStatus('sending');

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email: newsletterEmail, formType: 'newsletter-signup' })
      });
      if (!res.ok) throw new Error(`Newsletter signup failed: ${res.status}`);

      setNewsletterStatus('done');
      setNewsletterEmail('');
    } catch (err) {
      setNewsletterStatus('error');
    }
  };
  
  return (
    <>
      {/* Page Header */}
      <section className="bg-dark text-white py-5 mt-5">
        <div className="container py-4">
          <div className="row">
            <div className="col-lg-8">
              <h1 className="display-5 fw-bold mb-3">Contact Us</h1>
              <p className="lead mb-0">Talk to us about a building assessment, energy and sustainability analysis, or MEP Tools.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section section-light">
        <div className="container">
          <div className="row g-5">
            <div className="col-lg-5" data-aos="fade-right">
              <h2 className="mb-4">Get In <span className="text-primary">Touch</span></h2>
              <p className="lead mb-4">Whether you want an assessment of an existing facility, an energy and sustainability analysis, or a look at MEP Tools — we'd like to hear about your project.</p>
              
              <div className="contact-info-card mb-4" data-aos="fade-up">
                <div className="contact-icon">
                  <i className="bi bi-envelope-fill"></i>
                </div>
                <div>
                  <h5 className="mb-1">Email Address</h5>
                  <p className="mb-0"><a href="mailto:info@sustainability-pros.com">info@sustainability-pros.com</a></p>
                </div>
              </div>

              <div className="contact-info-card mb-4" data-aos="fade-up" data-aos-delay="100">
                <div className="contact-icon">
                  <i className="bi bi-geo-alt-fill"></i>
                </div>
                <div>
                  <h5 className="mb-1">Location</h5>
                  <p className="mb-0">Raleigh-Durham Triangle region, NC — working with clients worldwide</p>
                </div>
              </div>

              <div className="contact-info-card" data-aos="fade-up" data-aos-delay="200">
                <div className="contact-icon">
                  <i className="bi bi-clock-fill"></i>
                </div>
                <div>
                  <h5 className="mb-1">Business Hours</h5>
                  <p className="mb-0">Monday - Friday: 8:00 AM - 6:00 PM</p>
                </div>
              </div>
            </div>
            
            <div className="col-lg-7" data-aos="fade-left">
              <div className="card shadow-lg border-0">
                <div className="card-body p-4 p-md-5">
                  <h3 className="card-title mb-4">Send Us a Message</h3>
                  
                  {formSubmitted && (
                    <div className="alert alert-success">
                      Thank you for your message! We'll get back to you soon.
                    </div>
                  )}

                  {submitError && (
                    <div className="alert alert-danger">
                      Sorry, something went wrong sending your message. Please email us directly at info@sustainability-pros.com.
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="form-floating mb-3">
                          <input 
                            type="text" 
                            className="form-control" 
                            id="name" 
                            name="name"
                            placeholder="Your Name" 
                            value={formData.name}
                            onChange={handleInputChange}
                            required 
                          />
                          <label htmlFor="name">Your Name</label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-floating mb-3">
                          <input 
                            type="email" 
                            className="form-control" 
                            id="email" 
                            name="email"
                            placeholder="Your Email" 
                            value={formData.email}
                            onChange={handleInputChange}
                            required 
                          />
                          <label htmlFor="email">Your Email</label>
                        </div>
                      </div>
                    </div>
                    
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="form-floating mb-3">
                          <input 
                            type="tel" 
                            className="form-control" 
                            id="phone" 
                            name="phone"
                            placeholder="Phone Number" 
                            value={formData.phone}
                            onChange={handleInputChange}
                          />
                          <label htmlFor="phone">Phone Number</label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-floating mb-3">
                          <select 
                            className="form-select" 
                            id="projectType" 
                            name="projectType"
                            value={formData.projectType}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="" disabled>Select Project Type</option>
                            <option value="Assessment">Existing Building / Facility Condition Assessment</option>
                            <option value="EnergyAnalysis">Energy & Sustainability Analysis</option>
                            <option value="DesignReview">Design Document Review</option>
                            <option value="Commissioning">Commissioning Support (New Construction / Renovation)</option>
                            <option value="MEPTools">MEP Tools Demo / Software</option>
                            <option value="Other">Other</option>
                          </select>
                          <label htmlFor="projectType">Project Type</label>
                        </div>
                      </div>
                    </div>
                    
                    <div className="form-floating mb-3">
                      <textarea 
                        className="form-control" 
                        id="message" 
                        name="message"
                        placeholder="Your Message" 
                        style={{ height: '150px' }}
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                      ></textarea>
                      <label htmlFor="message">Your Message</label>
                    </div>
                    
                    <div className="d-grid">
                      <button type="submit" className="btn btn-primary btn-lg" disabled={submitting}>
                        {submitting ? 'Sending…' : <>Send Message <i className="bi bi-send ms-2"></i></>}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section section-light">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">Frequently Asked <span className="text-primary">Questions</span></h2>
              <p className="lead">Find answers to common questions about working with us.</p>
            </div>
          </div>
          
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="accordion" id="contactAccordion">
                <div className="accordion-item border-0 mb-4 shadow-sm" data-aos="fade-up">
                  <h3 className="accordion-header">
                    <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#faq1">
                      What information should I have ready for the initial consultation?
                    </button>
                  </h3>
                  <div id="faq1" className="accordion-collapse collapse show" data-bs-parent="#contactAccordion">
                    <div className="accordion-body">
                      <p className="mb-0">Whatever you have is a fine starting point. For an existing building assessment, useful items include building age and size, equipment lists or O&M manuals, known problem areas, and recent utility bills. For new construction or renovation, design drawings and specifications help us scope design review or commissioning support. Missing documentation is common — recreating it is part of what we do.</p>
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item border-0 mb-4 shadow-sm" data-aos="fade-up" data-aos-delay="100">
                  <h3 className="accordion-header">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq2">
                      When is the right time to have our building assessed?
                    </button>
                  </h3>
                  <div id="faq2" className="accordion-collapse collapse" data-bs-parent="#contactAccordion">
                    <div className="accordion-body">
                      <p className="mb-0">Any time — but it pays off most before you build a capital plan, when energy costs are rising, or when equipment is nearing end of life. An assessment turns guesswork into a prioritized, cost-saving plan. For projects still under design or construction, we can also review documents early — when issues are cheap to fix on paper — and provide commissioning support where it's required.</p>
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item border-0 mb-4 shadow-sm" data-aos="fade-up" data-aos-delay="200">
                  <h3 className="accordion-header">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq3">
                      What do we receive at the end of an assessment?
                    </button>
                  </h3>
                  <div id="faq3" className="accordion-collapse collapse" data-bs-parent="#contactAccordion">
                    <div className="accordion-body">
                      <p className="mb-0">A written report documenting the condition of your mechanical, electrical, and plumbing systems, with photos, prioritized deficiencies, and recommended actions for both immediate repairs and capital planning. Unlike a typical closeout binder, our documentation is designed to stay useful — asset data can live on in MEP Tools, where it stays current as your building changes.</p>
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item border-0 shadow-sm" data-aos="fade-up" data-aos-delay="300">
                  <h3 className="accordion-header">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq4">
                      What geographical areas do you serve?
                    </button>
                  </h3>
                  <div id="faq4" className="accordion-collapse collapse" data-bs-parent="#contactAccordion">
                    <div className="accordion-body">
                      <p className="mb-0">We're based in the Raleigh-Durham Triangle region of North Carolina and work with clients across the globe. On-site work like assessments, commissioning, and field testing can be arranged wherever your facility is, and design document review and MEP Tools are fully remote-friendly.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="section bg-primary text-white">
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <h2 className="mb-4">Subscribe to Our Newsletter</h2>
              <p className="lead mb-5">Stay updated with the latest sustainable construction trends, projects, and tips.</p>
              
              {newsletterStatus === 'done' ? (
                <div className="alert alert-light d-inline-block">
                  Thanks — you're on the list!
                </div>
              ) : (
                <form className="row g-2 justify-content-center" onSubmit={handleNewsletterSubmit}>
                  <div className="col-md-8">
                    <div className="form-floating">
                      <input
                        type="email"
                        className="form-control"
                        id="newsletterEmail"
                        placeholder="Your Email"
                        value={newsletterEmail}
                        onChange={(e) => setNewsletterEmail(e.target.value)}
                        required
                      />
                      <label htmlFor="newsletterEmail" className="text-dark">Your Email Address</label>
                    </div>
                  </div>
                  <div className="col-md-auto">
                    <button type="submit" className="btn btn-light btn-lg h-100" disabled={newsletterStatus === 'sending'}>
                      {newsletterStatus === 'sending' ? 'Subscribing…' : 'Subscribe'}
                    </button>
                  </div>
                </form>
              )}
              {newsletterStatus === 'error' && (
                <p className="small mt-3 mb-0">Something went wrong — please try again or email info@sustainability-pros.com.</p>
              )}
              
              <p className="small mt-3 mb-0">By subscribing, you agree to receive our newsletter and marketing emails. You can unsubscribe at any time.</p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;